// Provider-agnostic session accessors.
//
// api.ts previously called Amplify's `Auth` directly. That is correct for the
// eight Cognito tenants and impossible for a GCIP tenant, where Amplify is
// never configured at all. These two helpers are the single seam: they dispatch
// on the tenant's authProvider so no call site has to know which IdP is behind
// the session.
//
// Return shapes intentionally mirror what the Amplify call sites already
// expected (a raw JWT string; a user object exposing `attributes.email`), so
// adopting them is a one-line change per site rather than a refactor.

import { Auth } from "aws-amplify";

import { getAuthProvider } from "../config/product";
import {
  forceRefresh as gcipForceRefresh,
  getCurrentUser as gcipUser,
  getIdToken as gcipIdToken,
  signOut as gcipSignOut,
} from "./gcip";

/** The ID token to send as Authorization. Throws if there is no session. */
export async function getIdTokenJwt(): Promise<string> {
  if (getAuthProvider() === "gcip") {
    const token = await gcipIdToken();
    if (!token) throw new Error("NO_SESSION");
    return token;
  }
  const session = await Auth.currentSession();
  return session.getIdToken().getJwtToken();
}

export interface SessionUser {
  username?: string;
  attributes?: Record<string, unknown> & { email?: string; name?: string };
}

/**
 * Current user in the shape the existing Amplify call sites already read.
 *
 * The `attributes` bag must carry `custom:aeronet_user_id` and
 * `custom:aeronet_company_id` under exactly those keys — call sites index them
 * directly (Sentry user context, and the `get-nav-<id>` axios cache key). GCIP
 * carries the identical claim names on its token, so the mapping is 1:1.
 */
export async function getSessionUser(): Promise<SessionUser | null> {
  if (getAuthProvider() === "gcip") {
    const u = gcipUser();
    if (!u) return null;
    return {
      username: u.email,
      attributes: {
        email: u.email,
        "custom:aeronet_user_id": u.aeronetUserId,
        "custom:aeronet_company_id": u.aeronetCompanyId,
      },
    };
  }
  return (await Auth.currentAuthenticatedUser()) as SessionUser;
}

/** Provider-aware sign-out. Amplify's Auth.signOut() is a no-op-that-throws on GCIP. */
export async function signOutSession(): Promise<void> {
  if (getAuthProvider() === "gcip") {
    gcipSignOut();
    return;
  }
  await Auth.signOut();
}

/**
 * Refresh the session NOW, ignoring the locally cached expiry. True when a
 * fresh ID token is stored and the next request will carry it.
 *
 * `Auth.currentSession()` (what getIdTokenJwt calls) only refreshes when
 * Amplify's own clock says the token has expired. When the backend has already
 * answered 401, that judgement was wrong (clock drift, a token that expired in
 * flight), so a second currentSession() would hand back the same rejected
 * token. Going to the refresh token directly is the only real retry.
 */
export async function refreshSession(): Promise<boolean> {
  if (getAuthProvider() === "gcip") return gcipForceRefresh();
  try {
    const user = await Auth.currentAuthenticatedUser();
    const refreshToken = user?.getSignInUserSession?.()?.getRefreshToken?.();
    if (!refreshToken?.getToken?.()) return false;
    await new Promise((resolve, reject) =>
      user.refreshSession(refreshToken, (err: unknown, session: unknown) =>
        err ? reject(err) : resolve(session)
      )
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Send the browser to the tenant's login screen. Cognito: the hosted UI (the
 * same call app/SignIn.tsx makes on a cold boot). GCIP: a reload, which lands
 * on GcipGate's sign-in form now that the session is gone.
 */
export function startSignIn(): void {
  if (getAuthProvider() === "gcip") {
    window.location.reload();
    return;
  }
  Auth.federatedSignIn().catch(() => {});
}
